import { useEffect, useMemo, useState } from "react";
import { api, fmtEur, fmtDateTime, BACKEND_URL, formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Wrench, Lock, FilePdf, Trash, MagnifyingGlass, CheckCircle, ArrowUUpLeft, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import NewServiceDialog from "./components/NewServiceDialog";

export default function Services() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [openNew, setOpenNew] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/services");
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const s = q.toLowerCase();
    return items.filter((it) =>
      it.license_plate.toLowerCase().includes(s) ||
      it.invoice_number.toLowerCase().includes(s) ||
      it.wash_type.toLowerCase().includes(s) ||
      it.operario_name?.toLowerCase().includes(s)
    );
  }, [items, q]);

  const markPaid = async (id) => {
    if (!window.confirm("¿Emitir factura definitiva?\n\nUna vez emitida, la factura queda INALTERABLE y solo podrá ANULARSE mediante una rectificativa (factura negativa).")) return;
    try {
      await api.put(`/services/${id}`, { status: "Pagado" });
      toast.success("Factura emitida (inalterable)");
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este borrador?\n\nSolo se pueden eliminar borradores. Las facturas emitidas solo pueden anularse con rectificativa.")) return;
    try {
      await api.delete(`/services/${id}`);
      toast.success("Borrador eliminado"); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const rectify = async (s) => {
    if (!window.confirm(`¿Anular factura ${s.invoice_number}?\n\nSe creará una factura RECTIFICATIVA con importes negativos que la cancela contablemente. La factura original quedará marcada como "Anulada" pero permanecerá en el histórico (obligación fiscal).`)) return;
    try {
      const { data } = await api.post(`/services/${s.id}/rectify`);
      toast.success(`Factura anulada · Rectificativa ${data.invoice_number} creada`);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const openPdf = (id) => {
    const url = `${BACKEND_URL}/api/services/${id}/pdf`;
    fetch(url, { credentials: "include" })
      .then((r) => r.blob())
      .then((b) => window.open(URL.createObjectURL(b), "_blank"))
      .catch(() => toast.error("No se pudo abrir el PDF"));
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500">
            {user.role === "admin" ? "Operativa diaria" : "Mis servicios"}
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tighter mt-1">Servicios</h1>
        </div>
        <div className="flex gap-2 items-center">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-[var(--ag-text-muted)]" />
            <Input className="pl-8 rounded-sm h-9 w-56" placeholder="Buscar matrícula, factura…"
              value={q} onChange={(e)=>setQ(e.target.value)} data-testid="services-search" />
          </div>
          <Button onClick={()=>setOpenNew(true)} className="bg-[var(--ag-gold)] hover:bg-[#FFCD3C] rounded-sm" data-testid="services-new-btn">
            <Wrench className="mr-1.5" weight="duotone" /> Nuevo
          </Button>
        </div>
      </header>

      <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50 hover:bg-gray-50">
              <TableHead className="text-[10px] uppercase tracking-[0.15em]">Factura</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.15em]">Fecha</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.15em]">Matrícula</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.15em]">Lavado</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.15em]">Operario</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.15em] text-right">Base</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.15em] text-right">IVA</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.15em] text-right">Total</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.15em]">Estado</TableHead>
              <TableHead className="text-right text-[10px] uppercase tracking-[0.15em]">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={10} className="text-center text-gray-500 py-10">Cargando…</TableCell></TableRow>}
            {!loading && filtered.length === 0 && <TableRow><TableCell colSpan={10} className="text-center text-gray-500 py-10">Sin servicios</TableCell></TableRow>}
            {filtered.map((s) => (
              <TableRow key={s.id} className={s.rectifies_id ? "bg-red-50/40" : (s.locked ? "locked-row" : "")} data-testid={`service-row-${s.id}`}>
                <TableCell className="font-mono-tab text-xs">
                  {s.invoice_number}
                  {s.rectifies_invoice && <div className="text-[10px] text-red-600">↺ rectifica {s.rectifies_invoice}</div>}
                  {s.rectified_invoice && <div className="text-[10px] text-amber-700">↻ rectificado por {s.rectified_invoice}</div>}
                </TableCell>
                <TableCell className="text-xs">{fmtDateTime(s.date)}</TableCell>
                <TableCell className="font-mono-tab font-semibold">{s.license_plate}</TableCell>
                <TableCell className="text-sm">
                  {s.wash_type}
                  {s.labor_amount != null && s.parts_amount != null && (
                    <div className="text-[9px] uppercase tracking-wider text-[var(--ag-gold)] font-semibold mt-0.5" data-testid={`service-pro-${s.id}`}>
                      Pro · {fmtEur(s.labor_amount)} mano obra + {fmtEur(s.parts_amount)} materiales
                    </div>
                  )}
                  {s.membership_used && (
                    <div className="text-[9px] uppercase tracking-wider text-purple-700 font-semibold mt-0.5">
                      💎 Membresía: {s.membership_used.plan_name || "Activa"}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-sm">{s.operario_name}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtEur(s.base_imponible)}</TableCell>
                <TableCell className="text-right tabular-nums text-gray-500">{fmtEur(s.iva_amount)}</TableCell>
                <TableCell className="text-right tabular-nums font-semibold">{fmtEur(s.total_price)}</TableCell>
                <TableCell>
                  {s.rectifies_id ? (
                    <Badge className="bg-red-100 text-red-800 hover:bg-red-100 rounded-sm gap-1" title="Esta factura ANULA otra previa (importes negativos)"><ArrowUUpLeft size={10} /> Anulación</Badge>
                  ) : s.rectified_by ? (
                    <Badge className="bg-gray-200 text-gray-700 hover:bg-gray-200 rounded-sm gap-1 line-through" title="Esta factura fue ANULADA por una rectificativa"><Lock size={10} /> Anulada</Badge>
                  ) : s.status === "Pagado" ? (
                    <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 rounded-sm gap-1" title="Factura emitida y pagada — Inalterable. Solo puede anularse con rectificativa."><Lock size={10} /> Emitida</Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 rounded-sm" title="Borrador pendiente — Puedes editar o eliminar antes de marcarla como Pagada">Borrador</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button size="icon" variant="ghost" onClick={()=>openPdf(s.id)} title="Ver/Descargar factura PDF" data-testid={`pdf-${s.id}`}>
                      <FilePdf size={16} />
                    </Button>
                    {!s.locked && (
                      <Button size="icon" variant="ghost" onClick={()=>markPaid(s.id)} title="Emitir factura (marcar como pagada e inalterable)" data-testid={`pay-${s.id}`}>
                        <CheckCircle size={16} className="text-emerald-600" />
                      </Button>
                    )}
                    {user.role === "admin" && s.locked && !s.rectifies_id && !s.rectified_by && (
                      <Button size="icon" variant="ghost" onClick={()=>rectify(s)} title="Anular factura (crea rectificativa negativa)" data-testid={`rectify-${s.id}`}>
                        <ArrowUUpLeft size={16} className="text-red-600" />
                      </Button>
                    )}
                    {user.role === "admin" && !s.locked && (
                      <Button size="icon" variant="ghost" onClick={()=>del(s.id)} title="Eliminar borrador" data-testid={`del-${s.id}`}>
                        <Trash size={16} className="text-red-600" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <NewServiceDialog open={openNew} onOpenChange={setOpenNew} onCreated={load} />
    </div>
  );
}
